import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useSetState } from '@campaignhub/react-hooks'
import { toggleArray } from '@campaignhub/javascript-utils'

import JSZip from 'jszip'
import axios from 'axios'
import { saveAs } from 'file-saver'
import swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { Box, Button, Checkbox, ListItem, Tag, Text } from '@campaignhub/suit-theme'

import { faFolderDownload, faPaperPlane } from '@fortawesome/pro-light-svg-icons'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import custom from '@styles/custom.module.scss'

import useCurrentUser from '@hooks/useCurrentUser'
import useServiceJobSendToEsoft from '@hooks/useServiceJobSendToEsoft'

import userTypes from '@enums/userTypes'

import ListView from './components/ListView'

const contentTypeExtensions = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/tiff': 'tif',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
}

const zipEntryName = (item) => {
  const name = item?.metadata?.name || item.name || 'file'
  if (name.lastIndexOf('.') > 0) {
    return name
  }

  const extension = contentTypeExtensions[item?.content_type]
  return extension ? `${name}.${extension}` : name
}

const uniqueEntryName = (name, taken) => {
  const dotAt = name.lastIndexOf('.')
  const base = dotAt > 0 ? name.slice(0, dotAt) : name
  const extension = dotAt > 0 ? name.slice(dotAt) : ''

  let entry = name
  let duplicate = 2
  while (taken.has(entry)) {
    entry = `${base} (${duplicate})${extension}`
    duplicate += 1
  }

  taken.add(entry)
  return entry
}

const defaultState = {
  selectedIds: [],
  zipCount: 0,
  zipLoading: false,
}

const ClientResources = (props) => {
  const { editable, resources, serviceJob } = props

  const [state, setState] = useSetState(defaultState)
  const { selectedIds, zipCount, zipLoading } = state

  const { currentUser } = useCurrentUser()
  const { callbacks: { postServiceJobSendToEsoft }, creating: sendToEsoftCreating } = useServiceJobSendToEsoft()

  const canSendToEsoft = ['Studio', 'BackOffice'].some(name => currentUser?.userRole?.userTypeId === userTypes.find(x => x.name === name).id)

  const selectAll = selectedIds.length > 0 && selectedIds.length === resources.length

  const selectContent = (selectedContent) => {
    setState({ selectedIds: toggleArray(selectedIds, selectedContent.id) || [] })
  }

  const zip = new JSZip()
  const takenPaths = new Set()
  const countRef = useRef()
  const totalRef = useRef()

  const download = item => axios.get(item.download_uri, { responseType: 'blob' }).then((resp) => {
    countRef.current.textContent = Object.values(zip.files).length
    zip.file(uniqueEntryName(zipEntryName(item), takenPaths), resp.data)
  })

  const downloadResources = (selectedResources) => {
    setState({ zipLoading: true, zipCount: selectedResources.length })
    let failed = 0
    const arrOfFiles = selectedResources.map(item => download(item).catch((err) => {
      failed += 1
      console.error(err)
      console.error(item)
    }))

    Promise.all(arrOfFiles)
      .then(() => {
        if (failed >= selectedResources.length) {
          toast.error('Failed to download the selected resources. See browser console for details.')
          setState({ zipLoading: false })
          return
        }

        if (failed > 0) {
          toast.warning(`${failed} of ${selectedResources.length} resources could not be downloaded`)
        }

        zip.generateAsync({ type: 'blob' }, (metadata) => {
          countRef.current.textContent = metadata.percent.toFixed(0)
          totalRef.current.textContent = '% Processing Zip'
        }).then((blob) => {
          saveAs(blob, `${serviceJob.campaign.name}.zip`)
          setState({ zipLoading: false })
        })
      })
      .catch((err) => {
        console.error(err)
        setState({ zipLoading: false })
      })
  }

  const sendToEsoft = () => {
    swal.fire({
      title: 'Send to Esoft',
      html: 'This action will send the selected client resources to Esoft.'
        + '<br/>Do you wish to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (!value) return

      postServiceJobSendToEsoft(serviceJob.id, { clientTab: true, ids: selectedIds.join(',') }).then(({ success, errors }) => {
        if (!success) {
          toast.warning(errors[0])
          return
        }

        setState({ selectedIds: [] })
      })
    })
  }

  return (
    <>
      {resources.length > 0 && (
        <Box flexDirection="column" fontSize="small" className={custom.scroll}>
          <Box
            alignItems="center"
            borderBottom="1px solid"
            borderColor="lineColor"
            flexDirection="row"
            padding="large"
            className={custom.root}
            style={{ justifyContent: 'flex-end' }}
          >
            {editable && (
              <Box
                alignItems="center"
                flexShrink="0"
                width="auto"
                style={{ marginRight: 6 }}
              >
                <Button
                  buttonStyle="secondary"
                  disabled={selectedIds.length < 1 || zipLoading}
                  marginRight="medium"
                  onClick={() => downloadResources(resources.filter(x => selectedIds.includes(x.id)))}
                  size="small"
                  title="Download"
                  width="auto"
                >
                  {zipLoading && (
                    <>
                      <Text ref={countRef} marginRight="small"> 0 </Text>
                      <Text ref={totalRef} marginRight="large"> / {zipCount} </Text>
                    </>
                  )}
                  <FontAwesomeIcon icon={zipLoading ? faSpinner : faFolderDownload} className={zipLoading ? 'fa-spin' : ''} />
                </Button>
                <Tag boxProps={{ width: 'fit-content', fontSize: 'xsmall', marginRight: 'large' }}>{selectedIds.length} Selected</Tag>
                <Checkbox checked={selectAll} onClick={() => setState({ selectedIds: selectAll ? [] : resources.map(content => content.id) })} />
              </Box>
            )}
          </Box>
          <Box flexDirection="column" padding="large" maxHeight="586px" overflowY="auto">
            {resources.map(item => (
              <ListView
                editable={editable}
                file={item}
                key={item.id}
                selected={selectedIds.includes(item.id)}
                selectContent={selectContent}
              />
            ))}
          </Box>
          {editable && canSendToEsoft && (
            <ListItem
              boxProps={{
                borderTop: '1px solid',
                padding: 'large',
                lineHeight: '1.3',
                alignItems: 'center',
              }}
              disableHover
            >
              <Box flexDirection="row" alignItems="center" width="100%">
                <Box alignItems="center" marginLeft="auto" justifyContent="flexEnd" width="auto">
                  <Button
                    size="medium"
                    buttonStyle="secondary"
                    disabled={serviceJob.serviceJobStatusId !== 50}
                    icon={<FontAwesomeIcon icon={faPaperPlane} />}
                    onClick={sendToEsoft}
                    loading={sendToEsoftCreating}
                  >
                    Send to Esoft
                  </Button>
                </Box>
              </Box>
            </ListItem>
          )}
        </Box>
      )}

      {!resources.length && (
        <Box flexDirection="column" fontSize="small" padding="large">
          <Box
            alignItems="center"
            color="bodyFontColor"
            display="grid"
            height={[150, 250]}
            justifyContent="center"
          >
            <Text fontSize="small" fontWeight="600">
              No client resources available.
            </Text>
          </Box>
        </Box>
      )}
    </>
  )
}

ClientResources.propTypes = {
  editable: PropTypes.bool,
  resources: PropTypes.array,
  serviceJob: PropTypes.object,
}

export default ClientResources
